<script setup>
import { computed, getCurrentInstance } from 'vue';
import { router } from '@inertiajs/vue3';
import Card from 'primevue/card';
import Button from 'primevue/button';
import { useToast } from 'primevue/usetoast';

const { proxy } = getCurrentInstance();
const $t = proxy.$t;
const toast = useToast();

const props = defineProps({
    orderNumber: { type: String, required: true },
    boletoUrl: { type: String, required: true },
    barcode: { type: String, default: null },
    expirationDate: { type: String, required: true },
    total: { type: Number, required: true },
    currency: { type: String, required: true },
    orderId: { type: Number, required: true },
});

const formattedExpiration = computed(() => {
    const date = new Date(props.expirationDate);
    return date.toLocaleDateString('pt-BR');
});

const formatTotal = () => {
    const value = props.total / 100;
    return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: props.currency,
        minimumFractionDigits: 2,
    }).format(value);
};

const copy = async (text, label) => {
    try {
        await navigator.clipboard.writeText(text);
        toast.add({ severity: 'success', summary: $t('Copied'), detail: `${label} ${$t('copied to clipboard')}`, life: 2000 });
    } catch {
        toast.add({ severity: 'warn', summary: $t('Error'), detail: $t('Could not copy'), life: 2000 });
    }
};

const openBoleto = () => window.open(props.boletoUrl, '_blank');

const goToOrder = () => {
    router.visit(proxy.route('client.orders.show', { order: props.orderId }));
};
</script>

<template>
    <Card class="boleto-instructions">
        <template #title>
            <div class="flex items-center gap-2">
                <i class="pi pi-barcode text-orange-600 text-xl"></i>
                <span>{{ $t('Pay with Boleto') }}</span>
            </div>
        </template>

        <template #content>
            <p class="text-surface-600 mb-5">
                {{ $t('Your boleto has been generated. Pay at any bank, lottery, or internet banking before the due date.') }}
            </p>

            <!-- Resumo -->
            <div class="border border-orange-200 rounded-xl p-5 bg-orange-50 mb-5 space-y-3">
                <div class="flex justify-between items-center">
                    <span class="text-sm text-surface-600">{{ $t('Amount') }}</span>
                    <span class="text-xl font-bold text-orange-700">{{ formatTotal() }}</span>
                </div>
                <div class="flex justify-between items-center">
                    <span class="text-sm text-surface-600">{{ $t('Due date') }}</span>
                    <span class="font-medium">{{ formattedExpiration }}</span>
                </div>
            </div>

            <!-- Código de barras -->
            <div v-if="barcode" class="bg-surface-50 border border-surface-200 rounded-xl p-4 mb-5">
                <div class="text-sm font-medium text-surface-600 mb-2">{{ $t('Barcode') }}</div>
                <code class="block text-xs bg-white border border-surface-200 rounded-lg p-3 break-all font-mono text-surface-700 select-all mb-3">
                    {{ barcode }}
                </code>
                <Button
                    :label="$t('Copy Barcode')"
                    icon="pi pi-copy"
                    class="w-full"
                    severity="secondary"
                    @click="copy(barcode, $t('Barcode'))"
                />
            </div>

            <!-- Abrir boleto -->
            <Button
                :label="$t('Open Boleto')"
                icon="pi pi-external-link"
                class="w-full mb-3"
                severity="success"
                @click="openBoleto"
            />

            <!-- Aviso compensação -->
            <div class="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800 space-y-2 mb-5">
                <p><i class="pi pi-info-circle mr-2"></i>{{ $t('Boleto payments may take up to 3 business days to be confirmed.') }}</p>
                <p><i class="pi pi-envelope text-blue-500 mr-2"></i>{{ $t('You will receive a confirmation email when the payment is processed.') }}</p>
            </div>

            <Button
                :label="$t('View Order Status')"
                icon="pi pi-arrow-right"
                class="w-full"
                severity="secondary"
                text
                @click="goToOrder"
            />
        </template>
    </Card>
</template>
